import React, { useState, useMemo } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  Church, 
  GraduationCap, 
  MapPin, 
  Brain,
  Heart,
  Filter
} from 'lucide-react';
import { GeneralStats } from './GeneralStats';
import { AgeDistributionStats } from './AgeDistributionStats';
import { CivilStatusStats } from './CivilStatusStats';
import { SpiritualStats } from './SpiritualStats';
import { AcademicStats } from './AcademicStats';
import { DemographicStats } from './DemographicStats';
import { PersonalityStats } from './PersonalityStats';
import { StatsFilters, type StatsFilters as StatsFiltersType } from './StatsFilters';

// Tipos de datos
interface StatsData {
  totalMembers: number;
  genderStats: {
    hombres: number;
    mujeres: number;
  };
  civilStatusStats: {
    soltero: number;
    soltera: number;
    casado: number;
    casada: number;
    viudo: number;
    viuda: number;
    divorciado: number;
    divorciada: number;
  };
  ageStats: {
    menoresDeEdad: number;
    mayoresDeEdad: number;
    detalleEdad: {
      ninos: number;
      adolescentes: number;
      jovenes: number;
      adultosJovenes: number;
      adultos: number;
      adultosMayores: number;
    };
  };
  zonaStats: Array<{ zona: string; count: number }>;
  documentoStats: {
    conDocumento: number;
    sinDocumento: number;
    porTipo: {
      cedula: number;
      tarjetaIdentidad: number;
      pasaporte: number;
      otro: number;
    };
  };
  registroPorMes: Array<{ mes: string; anio: number; count: number }>;
  spiritualStats: {
    totalConDatosComplementarios: number;
    bautizados: number;
    noBautizados: number;
    oracionFe: number;
    sinOracionFe: number;
    perteneceMinisterio: number;
    noPerteneceMinisterio: number;
    topMinisterios: Array<{ ministerio: string; count: number }>;
    topHabilidades: Array<{ habilidad: string; count: number }>;
    topDones: Array<{ don: string; count: number }>;
    distribucionTiempoAsistencia: Array<{ tiempo: string; count: number }>;
  };
  academicStats: {
    distribucionNivelEstudios: Array<{ nivel: string; count: number }>;
    topProfesiones: Array<{ profesion: string; count: number }>;
    conProfesion: number;
    sinProfesion: number;
  };
  personalityStats: {
    distribucionPersonalidad: Array<{ tipo: string; count: number }>;
  };
}

interface StatsDashboardProps {
  stats?: StatsData | null;
  isLoading?: boolean;
}

type TabType = 'general' | 'spiritual' | 'academic' | 'demographic' | 'personality';

// Función para calcular estadísticas filtradas
const calculateFilteredStats = (stats: StatsData, filters: StatsFiltersType): StatsData => {
  // Calcular el factor de escala basado en los filtros
  let scaleFactor = 1;
  let filteredTotal = stats.totalMembers;

  // Aplicar filtro de género
  if (filters.gender === 'male') {
    filteredTotal = stats.genderStats.hombres;
    scaleFactor = stats.genderStats.hombres / stats.totalMembers;
  } else if (filters.gender === 'female') {
    filteredTotal = stats.genderStats.mujeres;
    scaleFactor = stats.genderStats.mujeres / stats.totalMembers;
  }

  // Aplicar filtro de rango de edad
  if (filters.ageRange !== 'all') {
    let ageCount = 0;
    switch (filters.ageRange) {
      case 'children':
        ageCount = stats.ageStats.detalleEdad.ninos + stats.ageStats.detalleEdad.adolescentes;
        break;
      case 'youth':
        ageCount = stats.ageStats.detalleEdad.jovenes;
        break;
      case 'adults':
        ageCount = stats.ageStats.detalleEdad.adultosJovenes + stats.ageStats.detalleEdad.adultos;
        break;
      case 'seniors':
        ageCount = stats.ageStats.detalleEdad.adultosMayores;
        break;
    }
    // Calcular el factor combinado
    const ageFactor = ageCount / stats.totalMembers;
    if (filters.gender !== 'all') {
      // Si ya hay un filtro de género, tomamos el mínimo
      scaleFactor = Math.min(scaleFactor, ageFactor);
      filteredTotal = Math.round(filteredTotal * ageFactor);
    } else {
      scaleFactor = ageFactor;
      filteredTotal = ageCount;
    }
  }

  // Aplicar filtro de datos complementarios
  if (filters.hasComplementaryData === 'yes') {
    filteredTotal = Math.min(filteredTotal, stats.spiritualStats.totalConDatosComplementarios);
    scaleFactor = filteredTotal / stats.totalMembers;
  } else if (filters.hasComplementaryData === 'no') {
    const sinDatos = stats.totalMembers - stats.spiritualStats.totalConDatosComplementarios;
    filteredTotal = Math.min(filteredTotal, sinDatos);
    scaleFactor = filteredTotal / stats.totalMembers;
  }

  // Aplicar filtro de bautismo
  if (filters.baptized === 'yes') {
    filteredTotal = Math.min(filteredTotal, stats.spiritualStats.bautizados);
    scaleFactor = filteredTotal / stats.totalMembers;
  } else if (filters.baptized === 'no') {
    filteredTotal = Math.min(filteredTotal, stats.spiritualStats.noBautizados);
    scaleFactor = filteredTotal / stats.totalMembers;
  }

  // Aplicar filtro de ministerio
  if (filters.inMinistry === 'yes') {
    filteredTotal = Math.min(filteredTotal, stats.spiritualStats.perteneceMinisterio);
    scaleFactor = filteredTotal / stats.totalMembers;
  } else if (filters.inMinistry === 'no') {
    filteredTotal = Math.min(filteredTotal, stats.spiritualStats.noPerteneceMinisterio);
    scaleFactor = filteredTotal / stats.totalMembers;
  }

  // Aplicar filtro de estado civil
  if (filters.civilStatus !== 'all') {
    let civilCount = 0;
    switch (filters.civilStatus) {
      case 'single':
        civilCount = stats.civilStatusStats.soltero + stats.civilStatusStats.soltera;
        break;
      case 'married':
        civilCount = stats.civilStatusStats.casado + stats.civilStatusStats.casada;
        break;
      case 'other':
        civilCount = stats.civilStatusStats.viudo + stats.civilStatusStats.viuda + 
                     stats.civilStatusStats.divorciado + stats.civilStatusStats.divorciada;
        break;
    }
    filteredTotal = Math.min(filteredTotal, civilCount);
    scaleFactor = filteredTotal / stats.totalMembers;
  }

  // Si no hay filtros activos, devolver los stats originales
  if (scaleFactor >= 0.99) {
    return stats;
  }

  // Función para escalar valores proporcionalmente
  const scale = (value: number) => Math.max(0, Math.round(value * scaleFactor));
  const scaleArray = (arr: Array<{ [key: string]: any; count: number }>) => 
    arr.map(item => ({ ...item, count: scale(item.count) }));

  // Calcular proporción de género en el segmento filtrado
  let maleRatio = stats.genderStats.hombres / stats.totalMembers;
  let femaleRatio = stats.genderStats.mujeres / stats.totalMembers;

  // Ajustar proporciones si hay filtro de género
  if (filters.gender === 'male') {
    maleRatio = 1;
    femaleRatio = 0;
  } else if (filters.gender === 'female') {
    maleRatio = 0;
    femaleRatio = 1;
  }

  return {
    ...stats,
    totalMembers: filteredTotal,
    genderStats: {
      hombres: Math.round(filteredTotal * maleRatio),
      mujeres: Math.round(filteredTotal * femaleRatio),
    },
    ageStats: {
      menoresDeEdad: scale(stats.ageStats.menoresDeEdad),
      mayoresDeEdad: scale(stats.ageStats.mayoresDeEdad),
      detalleEdad: {
        ninos: scale(stats.ageStats.detalleEdad.ninos),
        adolescentes: scale(stats.ageStats.detalleEdad.adolescentes),
        jovenes: scale(stats.ageStats.detalleEdad.jovenes),
        adultosJovenes: scale(stats.ageStats.detalleEdad.adultosJovenes),
        adultos: scale(stats.ageStats.detalleEdad.adultos),
        adultosMayores: scale(stats.ageStats.detalleEdad.adultosMayores),
      }
    },
    civilStatusStats: {
      soltero: scale(stats.civilStatusStats.soltero),
      soltera: scale(stats.civilStatusStats.soltera),
      casado: scale(stats.civilStatusStats.casado),
      casada: scale(stats.civilStatusStats.casada),
      viudo: scale(stats.civilStatusStats.viudo),
      viuda: scale(stats.civilStatusStats.viuda),
      divorciado: scale(stats.civilStatusStats.divorciado),
      divorciada: scale(stats.civilStatusStats.divorciada),
    },
    zonaStats: scaleArray(stats.zonaStats),
    documentoStats: {
      conDocumento: scale(stats.documentoStats.conDocumento),
      sinDocumento: scale(stats.documentoStats.sinDocumento),
      porTipo: {
        cedula: scale(stats.documentoStats.porTipo.cedula),
        tarjetaIdentidad: scale(stats.documentoStats.porTipo.tarjetaIdentidad),
        pasaporte: scale(stats.documentoStats.porTipo.pasaporte),
        otro: scale(stats.documentoStats.porTipo.otro),
      }
    },
    registroPorMes: scaleArray(stats.registroPorMes),
    spiritualStats: {
      totalConDatosComplementarios: scale(stats.spiritualStats.totalConDatosComplementarios),
      bautizados: scale(stats.spiritualStats.bautizados),
      noBautizados: scale(stats.spiritualStats.noBautizados),
      oracionFe: scale(stats.spiritualStats.oracionFe),
      sinOracionFe: scale(stats.spiritualStats.sinOracionFe),
      perteneceMinisterio: scale(stats.spiritualStats.perteneceMinisterio),
      noPerteneceMinisterio: scale(stats.spiritualStats.noPerteneceMinisterio),
      topMinisterios: scaleArray(stats.spiritualStats.topMinisterios),
      topHabilidades: scaleArray(stats.spiritualStats.topHabilidades),
      topDones: scaleArray(stats.spiritualStats.topDones),
      distribucionTiempoAsistencia: scaleArray(stats.spiritualStats.distribucionTiempoAsistencia),
    },
    academicStats: {
      distribucionNivelEstudios: scaleArray(stats.academicStats.distribucionNivelEstudios),
      topProfesiones: scaleArray(stats.academicStats.topProfesiones),
      conProfesion: scale(stats.academicStats.conProfesion),
      sinProfesion: scale(stats.academicStats.sinProfesion),
    },
    personalityStats: {
      distribucionPersonalidad: scaleArray(stats.personalityStats.distribucionPersonalidad),
    }
  };
};

export const StatsDashboard: React.FC<StatsDashboardProps> = ({ stats, isLoading }) => {
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [filters, setFilters] = useState<StatsFiltersType>({
    gender: 'all',
    ageRange: 'all',
    hasComplementaryData: 'all',
    baptized: 'all',
    inMinistry: 'all',
    civilStatus: 'all'
  });

  // Valores por defecto para evitar errores
  const baseStats: StatsData = {
    totalMembers: stats?.totalMembers || 0,
    genderStats: {
      hombres: stats?.genderStats?.hombres || 0,
      mujeres: stats?.genderStats?.mujeres || 0,
    },
    civilStatusStats: {
      soltero: stats?.civilStatusStats?.soltero || 0,
      soltera: stats?.civilStatusStats?.soltera || 0,
      casado: stats?.civilStatusStats?.casado || 0,
      casada: stats?.civilStatusStats?.casada || 0,
      viudo: stats?.civilStatusStats?.viudo || 0,
      viuda: stats?.civilStatusStats?.viuda || 0,
      divorciado: stats?.civilStatusStats?.divorciado || 0,
      divorciada: stats?.civilStatusStats?.divorciada || 0,
    },
    ageStats: {
      menoresDeEdad: stats?.ageStats?.menoresDeEdad || 0,
      mayoresDeEdad: stats?.ageStats?.mayoresDeEdad || 0,
      detalleEdad: {
        ninos: stats?.ageStats?.detalleEdad?.ninos || 0,
        adolescentes: stats?.ageStats?.detalleEdad?.adolescentes || 0,
        jovenes: stats?.ageStats?.detalleEdad?.jovenes || 0,
        adultosJovenes: stats?.ageStats?.detalleEdad?.adultosJovenes || 0,
        adultos: stats?.ageStats?.detalleEdad?.adultos || 0,
        adultosMayores: stats?.ageStats?.detalleEdad?.adultosMayores || 0,
      }
    },
    zonaStats: stats?.zonaStats || [],
    documentoStats: {
      conDocumento: stats?.documentoStats?.conDocumento || 0,
      sinDocumento: stats?.documentoStats?.sinDocumento || 0,
      porTipo: {
        cedula: stats?.documentoStats?.porTipo?.cedula || 0,
        tarjetaIdentidad: stats?.documentoStats?.porTipo?.tarjetaIdentidad || 0,
        pasaporte: stats?.documentoStats?.porTipo?.pasaporte || 0,
        otro: stats?.documentoStats?.porTipo?.otro || 0,
      }
    },
    registroPorMes: stats?.registroPorMes || [],
    spiritualStats: {
      totalConDatosComplementarios: stats?.spiritualStats?.totalConDatosComplementarios || 0,
      bautizados: stats?.spiritualStats?.bautizados || 0,
      noBautizados: stats?.spiritualStats?.noBautizados || 0,
      oracionFe: stats?.spiritualStats?.oracionFe || 0,
      sinOracionFe: stats?.spiritualStats?.sinOracionFe || 0,
      perteneceMinisterio: stats?.spiritualStats?.perteneceMinisterio || 0,
      noPerteneceMinisterio: stats?.spiritualStats?.noPerteneceMinisterio || 0,
      topMinisterios: stats?.spiritualStats?.topMinisterios || [],
      topHabilidades: stats?.spiritualStats?.topHabilidades || [],
      topDones: stats?.spiritualStats?.topDones || [],
      distribucionTiempoAsistencia: stats?.spiritualStats?.distribucionTiempoAsistencia || []
    },
    academicStats: {
      distribucionNivelEstudios: stats?.academicStats?.distribucionNivelEstudios || [],
      topProfesiones: stats?.academicStats?.topProfesiones || [],
      conProfesion: stats?.academicStats?.conProfesion || 0,
      sinProfesion: stats?.academicStats?.sinProfesion || 0
    },
    personalityStats: {
      distribucionPersonalidad: stats?.personalityStats?.distribucionPersonalidad || []
    }
  };

  // Calcular estadísticas filtradas
  const filteredStats = useMemo(() => {
    return calculateFilteredStats(baseStats, filters);
  }, [baseStats, filters]);

  // Verificar si hay filtros activos
  const hasActiveFilters = 
    filters.gender !== 'all' ||
    filters.ageRange !== 'all' ||
    filters.hasComplementaryData !== 'all' ||
    filters.baptized !== 'all' ||
    filters.inMinistry !== 'all' ||
    filters.civilStatus !== 'all';

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton para tabs */}
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse flex-shrink-0"></div>
          ))}
        </div>
        {/* Skeleton para contenido */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general' as TabType, label: 'General', icon: LayoutDashboard },
    { id: 'spiritual' as TabType, label: 'Espiritual', icon: Church },
    { id: 'academic' as TabType, label: 'Académico', icon: GraduationCap },
    { id: 'demographic' as TabType, label: 'Demográfico', icon: MapPin },
    { id: 'personality' as TabType, label: 'Personalidad', icon: Brain },
  ];

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <StatsFilters
        filters={filters}
        onChange={setFilters}
        totalResults={filteredStats.totalMembers}
        isLoading={isLoading}
      />

      {/* Indicador de filtros activos */}
      {hasActiveFilters && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-purple-600" />
            <div>
              <p className="text-xs font-medium text-purple-900">
                Mostrando datos filtrados
              </p>
              <p className="text-xs text-purple-700">
                {filteredStats.totalMembers.toLocaleString()} de {baseStats.totalMembers.toLocaleString()} miembros ({((filteredStats.totalMembers / Math.max(baseStats.totalMembers, 1)) * 100).toFixed(1)}%)
              </p>
            </div>
          </div>
          <div className="text-xs text-purple-600 bg-white px-3 py-1.5 rounded-full border border-purple-200">
            Datos estimados
          </div>
        </div>
      )}

      {/* Tabs de navegación */}
      <div className="flex space-x-2 overflow-x-auto pb-2 border-b border-gray-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-purple-700 border-b-2 border-purple-700 bg-purple-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Contenido según el tab activo - usando datos filtrados */}
      <div className="space-y-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <GeneralStats
              totalMembers={filteredStats.totalMembers}
              genderStats={filteredStats.genderStats}
              ageStats={filteredStats.ageStats}
            />
            <AgeDistributionStats
              detalleEdad={filteredStats.ageStats.detalleEdad}
              totalMembers={filteredStats.totalMembers}
            />
            <CivilStatusStats
              civilStatusStats={filteredStats.civilStatusStats}
              totalMembers={filteredStats.totalMembers}
            />
          </div>
        )}

        {activeTab === 'spiritual' && (
          <SpiritualStats
            spiritualStats={filteredStats.spiritualStats}
            totalMembers={filteredStats.totalMembers}
          />
        )}

        {activeTab === 'academic' && (
          <AcademicStats
            academicStats={filteredStats.academicStats}
            totalConDatosComplementarios={filteredStats.spiritualStats.totalConDatosComplementarios}
          />
        )}

        {activeTab === 'demographic' && (
          <DemographicStats
            zonaStats={filteredStats.zonaStats}
            documentoStats={filteredStats.documentoStats}
            registroPorMes={filteredStats.registroPorMes}
            totalMembers={filteredStats.totalMembers}
          />
        )}

        {activeTab === 'personality' && (
          <PersonalityStats
            personalityStats={filteredStats.personalityStats}
            totalConDatosComplementarios={filteredStats.spiritualStats.totalConDatosComplementarios}
          />
        )}
      </div>
    </div>
  );
};

export default StatsDashboard;
